import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { FiBell, FiRefreshCw, FiAlertTriangle, FiTarget, FiDollarSign, FiTrash2, FiInfo } from 'react-icons/fi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const notificationTypeConfig = {
  due_date: { label: 'Vencimento', icon: FiDollarSign, color: 'text-red-600', bg: 'bg-red-50', tooltip: 'Conta a pagar com vencimento em 1 dia' },
  goal_deadline: { label: 'Meta', icon: FiTarget, color: 'text-blue-900', bg: 'bg-blue-50', tooltip: 'Meta com prazo de 7 dias ou menos' },
  negative_balance: { label: 'Saldo Negativo', icon: FiAlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', tooltip: 'Suas despesas do mês superaram suas receitas' },
  payment_failed: { label: 'Falha no Pagamento', icon: FiAlertTriangle, color: 'text-red-600', bg: 'bg-red-50', tooltip: 'Não foi possível processar o pagamento automático da assinatura' },
  pre_expiry_warning: { label: 'Vencimento da Assinatura', icon: FiAlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', tooltip: 'Sua assinatura vence amanhã' },
  auto_renew_attempt: { label: 'Renovação Automática', icon: FiRefreshCw, color: 'text-green-600', bg: 'bg-green-50', tooltip: 'Tentativa de renovação automática da assinatura' }
};

export const Notificacoes = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/notifications/history`, { withCredentials: true });
      setHistory(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const response = await axios.get(`${API}/notifications/check`, { withCredentials: true });
      const data = response.data;
      if (data.sent_today > 0) {
        toast.success(`${data.sent_today} notificação(ões) enviada(s)!`);
      } else {
        toast.info('Nenhuma notificação pendente no momento.');
      }
      loadHistory();
    } catch (error) {
      console.error('Error checking notifications:', error);
      toast.error('Erro ao verificar notificações');
    } finally {
      setChecking(false);
    }
  };

  const handleDeleteOne = async (notification) => {
    try {
      await axios.delete(`${API}/notifications/by-key`, {
        withCredentials: true,
        data: { created_at: notification.created_at, type: notification.type }
      });
      setHistory(prev => prev.filter(n => !(n.created_at === notification.created_at && n.type === notification.type)));
      toast.success('Notificação excluída');
    } catch (error) {
      toast.error('Erro ao excluir notificação');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Excluir todas as notificações? Esta ação não pode ser desfeita.')) return;
    try {
      const r = await axios.delete(`${API}/notifications/all`, { withCredentials: true });
      setHistory([]);
      toast.success(r.data.message || 'Notificações excluídas');
    } catch (error) {
      toast.error('Erro ao excluir notificações');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatBRL = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />

      <main className="transition-all duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-4xl font-black text-blue-900">Notificações</h1>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="notifications-info-button" type="button" className="text-blue-900/60 hover:text-blue-900 transition-colors" aria-label="Como funcionam as notificações?">
                    <FiInfo size={20} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="text-sm w-80" data-testid="notifications-info-popover">
                  <p className="font-semibold text-blue-900 mb-2">Como funcionam as notificações?</p>
                  <ul className="space-y-1 list-disc list-inside text-slate-600">
                    <li><strong>Vencimento:</strong> 1 dia antes do vencimento de contas a pagar</li>
                    <li><strong>Metas:</strong> quando uma meta está a 7 dias ou menos do prazo</li>
                    <li><strong>Saldo Negativo:</strong> quando seu saldo mensal fica negativo</li>
                    <li><strong>Vencimento da Assinatura:</strong> aviso 1 dia antes do vencimento</li>
                    <li><strong>Renovação Automática:</strong> cobrança 1 dia antes do vencimento</li>
                  </ul>
                  <p className="mt-2 text-xs text-slate-500">Notificações com mais de 60 dias são apagadas automaticamente.</p>
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-slate-700">Histórico de alertas enviados por email</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="check-notifications-button"
              onClick={handleCheckNow}
              disabled={checking}
              className="bg-blue-900 hover:bg-blue-800 text-white inline-flex items-center gap-2"
            >
              <FiRefreshCw size={18} className={checking ? 'animate-spin' : ''} />
              {checking ? 'Verificando...' : 'Verificar Agora'}
            </Button>
            {history.length > 0 && (
              <Button
                data-testid="delete-all-notifications-button"
                onClick={handleDeleteAll}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-2"
                title="Apagar todas as notificações"
              >
                <FiTrash2 size={18} />
                Apagar todas
              </Button>
            )}
          </div>
        </div>

        {/* History */}
        {history.length === 0 ? (
          <div className="text-center py-16">
            <FiBell className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhuma notificação enviada</h3>
            <p className="text-slate-500 text-sm">As notificações aparecerão aqui quando forem enviadas por email.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((notification, index) => {
              const config = notificationTypeConfig[notification.type] || notificationTypeConfig.due_date;
              const Icon = config.icon;
              return (
                <Card
                  key={`${notification.created_at}-${notification.type}-${index}`}
                  data-testid={`notification-${index}`}
                  className="border border-blue-900/10 group"
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}
                        title={config.tooltip}
                      >
                        <Icon className={config.color} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-xs font-bold px-2 py-0.5 rounded ${config.bg} ${config.color} inline-flex items-center gap-1`}
                            title={config.tooltip}
                          >
                            {config.label}
                            <FiInfo size={12} className="opacity-60" />
                          </span>
                          <span className="text-xs text-slate-500">{formatDate(notification.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">
                          {notification.type === 'due_date' && `${notification.count} conta(s) a pagar com vencimento próximo`}
                          {notification.type === 'goal_deadline' && `${notification.count} meta(s) próxima(s) do prazo`}
                          {notification.type === 'negative_balance' && `Saldo negativo do mês: R$ ${formatBRL(notification.saldo)}`}
                          {notification.type === 'payment_failed' && `Falha no pagamento automático do cartão`}
                          {notification.type === 'pre_expiry_warning' && `Sua assinatura vence amanhã - lembrete enviado`}
                          {notification.type === 'auto_renew_attempt' && `Renovação automática processada`}
                        </p>
                      </div>
                      <button
                        data-testid={`delete-notification-${index}`}
                        onClick={() => handleDeleteOne(notification)}
                        className="opacity-60 hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity flex-shrink-0 p-2"
                        title="Excluir notificação"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      </main>
    </div>
  );
};
